import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button, Card, CardBody, Spinner } from '@nextui-org/react';
import {
  ArrowLeft,
  Clock,
  User,
  Share2,
  Link as LinkIcon,
  ExternalLink,
  Lock,
  FileText,
  Info,
} from 'lucide-react';
import newsService, { NewsArticle } from '../services/newsService';
import newsPreviewService, {
  NewsPreviewSettings,
} from '../services/newsPreviewService';

const ACCENT = '#3fced1';

const NewsDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [previewSettings, setPreviewSettings] = useState<NewsPreviewSettings>({
    percentage: 100,
    enabled: false,
  });

  const loadArticle = useCallback(async () => {
    if (!slug) {
      setError('Ungültiger Artikel-Link');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await newsService.getNewsBySlug(slug);
      if (response?.success && response.data) {
        setArticle(response.data);
      } else {
        setError('Artikel nicht gefunden');
        setArticle(null);
      }
    } catch (err) {
      console.error('Fehler beim Laden des Artikels:', err);
      setError('Fehler beim Laden des Artikels');
      setArticle(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadArticle();
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [loadArticle]);

  useEffect(() => {
    let cancelled = false;
    newsPreviewService.getSettings().then((settings) => {
      if (!cancelled) setPreviewSettings(settings);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCopyLink = async () => {
    try {
      const url = `${window.location.origin}/news/${article?.slug}`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error('Clipboard error:', e);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Card className="bg-zinc-900 border border-zinc-800">
          <CardBody className="p-10 flex flex-col items-center text-center">
            <FileText size={64} className="text-gray-500 mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">
              {error || 'Artikel nicht gefunden'}
            </h2>
            <p className="text-sm text-gray-400 mb-6">
              Der angeforderte Artikel existiert nicht oder wurde entfernt.
            </p>
            <Button
              onClick={() => navigate('/')}
              startContent={<ArrowLeft size={16} />}
              style={{ backgroundColor: ACCENT, color: '#000' }}
            >
              Zurück zur Startseite
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  const title = newsService.formatTitle(article);
  const description = newsService.formatDescription(article);
  const imageUrl = newsService.getImageUrl(article);
  const formattedDate = newsService.formatDate(article);
  const canonicalUrl = `https://dartcloudapp.de/news/${article.slug}`;
  const hasContent =
    !!(article.content && article.content.trim()) ||
    !!(article.contentHtml && article.contentHtml.trim());
  const hasTags = !!(article.tags && article.tags.length > 0);

  const rawContent = article.content?.trim() ?? '';
  const rawHtml = article.contentHtml?.trim() ?? '';
  const sourceForTruncation = rawContent || rawHtml.replace(/<[^>]*>/g, '');
  const isTruncated = newsPreviewService.isContentTruncated(
    sourceForTruncation,
    previewSettings
  );

  const renderContent = () => {
    if (rawHtml) {
      const displayHtml = isTruncated
        ? newsPreviewService.truncateHtml(rawHtml, previewSettings)
        : rawHtml;
      return (
        <div className="relative">
          <div
            className="prose prose-invert max-w-none text-gray-300 leading-relaxed text-[15px]"
            dangerouslySetInnerHTML={{ __html: displayHtml }}
          />
          {isTruncated && (
            <div
              className="pointer-events-none absolute left-0 right-0 bottom-0 h-24"
              style={{
                background:
                  'linear-gradient(to bottom, rgba(24,24,27,0) 0%, rgba(24,24,27,1) 100%)',
              }}
            />
          )}
        </div>
      );
    }
    if (rawContent) {
      const displayContent = isTruncated
        ? newsPreviewService.truncateContent(rawContent, previewSettings)
        : rawContent;
      return (
        <div className="relative">
          <div className="text-gray-300 leading-relaxed text-[15px] whitespace-pre-wrap">
            {displayContent}
            {isTruncated ? '…' : ''}
          </div>
          {isTruncated && (
            <div
              className="pointer-events-none absolute left-0 right-0 bottom-0 h-24"
              style={{
                background:
                  'linear-gradient(to bottom, rgba(24,24,27,0) 0%, rgba(24,24,27,1) 100%)',
              }}
            />
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <Helmet>
        <title>{`${title} | DartCloud News`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        {imageUrl && <meta property="og:image" content={imageUrl} />}
      </Helmet>

      <article className="w-full">
        {/* Hero Section */}
        <section className="border-b border-zinc-800 bg-gradient-to-br from-zinc-900 to-zinc-950">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-8 sm:py-10">
            <Button
              as={RouterLink}
              to="/"
              variant="light"
              startContent={<ArrowLeft size={16} />}
              className="text-white px-0 mb-4"
            >
              Zurück zur Startseite
            </Button>

            <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-gray-400">
              {article.source && (
                <span
                  className="px-2.5 py-1 rounded-md text-xs font-semibold"
                  style={{
                    backgroundColor: `${ACCENT}1A`,
                    color: ACCENT,
                  }}
                >
                  {article.source}
                </span>
              )}
              {formattedDate && (
                <span className="flex items-center gap-1">
                  <Clock size={14} />
                  {formattedDate}
                </span>
              )}
              {article.author && (
                <span className="flex items-center gap-1">
                  <User size={14} />
                  {article.author}
                </span>
              )}
            </div>

            <h1 className="text-2xl sm:text-4xl font-extrabold text-white leading-tight mb-4">
              {title}
            </h1>

            {description && (
              <p className="text-base sm:text-lg text-gray-300 leading-relaxed max-w-4xl">
                {description}
              </p>
            )}
          </div>
        </section>

        {/* Content */}
        <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Article (70%) */}
            <div className="flex-1 lg:basis-[70%]">
              <Card className="bg-zinc-900 border border-zinc-800 overflow-hidden">
                <CardBody className="p-0">
                  {imageUrl && (
                    <img
                      src={imageUrl}
                      alt={title}
                      className="w-full h-[250px] sm:h-[350px] object-cover"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).src = '/icon.png';
                      }}
                    />
                  )}

                  <div className="p-6">
                    {hasContent ? (
                      <>
                        {renderContent()}
                        {isTruncated && (
                          <div className="mt-6">
                            <PaywallCta article={article} />
                          </div>
                        )}
                      </>
                    ) : !article.contentCrawled ? (
                      <div className="flex items-center gap-3 p-4 rounded-lg bg-zinc-800/70 text-gray-400 text-sm">
                        <Info size={18} className="shrink-0" />
                        <span>Der vollständige Artikel wird gerade geladen...</span>
                      </div>
                    ) : article.sourceUrl ? (
                      <div className="mt-2">
                        <PaywallCta article={article} />
                      </div>
                    ) : null}

                    {!isTruncated && hasTags && (
                      <div className="mt-6 flex flex-wrap gap-2">
                        {article.tags!.map((tag) => (
                          <span
                            key={tag}
                            className="px-3 py-1.5 rounded-full text-xs bg-zinc-800 text-gray-400"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </div>

            {/* Sidebar (30%) */}
            <aside className="w-full lg:basis-[30%] lg:max-w-sm">
              <ShareCard
                article={article}
                copied={copied}
                onCopy={handleCopyLink}
              />
            </aside>
          </div>
        </div>
      </article>
    </>
  );
};

const PaywallCta: React.FC<{ article: NewsArticle }> = ({ article }) => {
  if (!article.sourceUrl) return null;
  return (
    <div
      className="p-6 rounded-xl bg-zinc-950/50 text-center"
      style={{ border: `2px solid ${ACCENT}4D` }}
    >
      <Lock size={48} style={{ color: ACCENT }} className="mx-auto mb-3" />
      <h3 className="text-lg font-bold text-white mb-2">
        Vollständigen Artikel lesen
      </h3>
      <p className="text-sm text-gray-400 mb-5">
        Besuche die Originalquelle, um den vollständigen Artikel zu lesen.
      </p>
      <Button
        as="a"
        href={article.sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        startContent={<ExternalLink size={16} />}
        className="w-full font-semibold"
        style={{ backgroundColor: ACCENT, color: '#000' }}
      >
        {`Artikel auf ${article.source || 'Quelle'} lesen`}
      </Button>
    </div>
  );
};

const ShareCard: React.FC<{
  article: NewsArticle;
  copied: boolean;
  onCopy: () => void;
}> = ({ article, copied, onCopy }) => {
  return (
    <Card className="bg-zinc-900 border border-zinc-800 sticky top-24">
      <CardBody className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <Share2 size={20} style={{ color: ACCENT }} />
          <h3 className="text-base font-bold text-white">Teilen</h3>
        </div>

        <Button
          onClick={onCopy}
          startContent={<LinkIcon size={16} />}
          className="w-full bg-zinc-800 text-white mb-3"
        >
          {copied ? 'Link kopiert!' : 'Link kopieren'}
        </Button>

        {article.sourceUrl && (
          <Button
            as="a"
            href={article.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            startContent={<ExternalLink size={16} />}
            className="w-full font-semibold"
            style={{ backgroundColor: ACCENT, color: '#000' }}
          >
            {`Artikel auf ${article.source || 'Quelle'} ansehen`}
          </Button>
        )}

        {article.source && (
          <p className="text-xs text-gray-500 mt-3">
            Quelle: {article.source}
          </p>
        )}
      </CardBody>
    </Card>
  );
};

export default NewsDetailPage;
